import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";

import { AppForm } from "components/Shared/AppForm";
import { InnerShadowBox } from "components/Shared/InnerShadowBox";

import { BASE_CONSTANTS } from "constants/baseConstants";

const INITIAL_STATE = {
  ar1Code: "",
  ar1Name: "",
  adminRegions2: [{ ar2Code: "", ar2Name: "" }],
};

export const AdministrativeRegionForm = ({
  region = null,
  isBusy = false,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "adminRegions2",
  });

  React.useEffect(() => {
    if (!region) return reset(INITIAL_STATE);

    reset({
      ...region,
      adminRegions2: region?.adminRegions2?.length
        ? region?.adminRegions2
        : [{ ar2Code: "", ar2Name: "" }],
    });
  }, [region, reset]);

  return (
    <AppForm loading={isBusy}>
      <InnerShadowBox>
        <AppForm.Content>
          <Grid container spacing={BASE_CONSTANTS.SPACING_MD}>
            <Grid item xs={6}>
              <Controller
                name="ar1Code"
                control={control}
                rules={{
                  required: "Please provide Code.",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Region Code"
                    size="small"
                    error={!!errors?.ar1Code}
                    helperText={errors?.ar1Code?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="ar1Name"
                control={control}
                rules={{
                  required: "Please provide Name.",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Region Name"
                    size="small"
                    error={!!errors?.ar1Name}
                    helperText={errors?.ar1Name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item sx={12}>
              <Box
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
                marginBottom={BASE_CONSTANTS.SPACING_SM}
              >
                <FormHelperText>Sub Regions</FormHelperText>

                <IconButton
                  onClick={() => append({ ar2Name: "", ar2Code: "" })}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                gap={BASE_CONSTANTS.SPACING_LG / 2}
              >
                {fields?.map((_, index) => (
                  <Box display="flex" gap={BASE_CONSTANTS.SPACING_SM}>
                    <Box display="flex" gap={BASE_CONSTANTS.SPACING_MD}>
                      <Controller
                        name={`adminRegions2.${index}.ar2Code`}
                        control={control}
                        rules={{
                          required: "Please provide Code.",
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Sub Region Code"
                            size="small"
                            error={!!errors["adminRegions2"]?.[index]?.ar2Code}
                            helperText={
                              errors["adminRegions2"]?.[index]?.ar2Code?.message
                            }
                          />
                        )}
                      />

                      <Controller
                        name={`adminRegions2.${index}.ar2Name`}
                        control={control}
                        rules={{
                          required: "Please provide Name.",
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Sub Region Name"
                            size="small"
                            error={!!errors["adminRegions2"]?.[index]?.ar2Name}
                            helperText={
                              errors["adminRegions2"]?.[index]?.ar2Name?.message
                            }
                          />
                        )}
                      />
                    </Box>

                    <IconButton
                      disabled={fields?.length < 2}
                      onClick={() => remove(index)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </AppForm.Content>
      </InnerShadowBox>

      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>

        <Button
          disabled={isBusy}
          variant="contained"
          onClick={handleSubmit(onSubmit)}
        >
          {region ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};
